<template>
  <v-card max-height="75hv">
    <v-toolbar
      color="indigo"
      dark
      dense
    >
      <v-toolbar-title>
        <v-icon left>{{ mdiMap }}</v-icon>
        {{ $vuetify.lang.t('$vuetify.map.travelMap.toolbar.map') }}
        <map-help></map-help>
      </v-toolbar-title>

      <critical-events-local-filters-menu
        :eventsLocalFilter="eventsLocalFilter"
        @local-critical-filter-changed="$emit('local-critical-filter-changed', $event)">
      </critical-events-local-filters-menu>

      <v-spacer></v-spacer>

      <tooltip-basic v-if="isDebugAvailable && tripPoints.length === 0"
                     :text="'$vuetify.map.travelMap.purePointCard.title'"
                     :icon="downloadIcon"
                     :emit-state="`pure-point-card-load`"
                     @pure-point-card-load="$emit('load_trip_points')"
                     :loading="tripPointsLoading">
      </tooltip-basic>

      <v-switch class="mt-5 ml-5" v-if="isDebugAvailable" v-model="rawValues" label="Raw values"
                color="primary"></v-switch>
    </v-toolbar>
    <v-card-text>
      <pure-point-card v-if="selectedTripPoints.length > 0" :selected-trip-points="selectedTripPoints"
                       :raw-values="rawValues" :datetimestart="travel.datetimestart.getTime()"
                       :user-id="travel.user.userId"
                       @clear-all-pure-points="clearAllPurePoints"
                       class="mb-2"></pure-point-card>

      <l-map ref="map" :zoom.sync="zoom" style="z-index: 0; position: relative" class="mapSize">
        <!--        <v-protobuf :url="url"></v-protobuf>-->
        <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>

        <l-control-layers
          v-if="isSpecial && !routeStatistics.isEmpty()"
          position="topright"
          :collapsed="true"
          :sort-layers="true"
        />

        <!-- path -->
        <l-geo-json ref="map_geojson" :geojson="path" @click="addPureEventMarker"></l-geo-json>

        <!-- pure event markers -->
        <l-marker v-for="(marker, index) in tripPointsMarkers" :key="index" :lat-lng="marker"
                  @click="removeMarker(index)"></l-marker>

        <!-- layers groups -->
        <l-layer-group v-for="entry in routeStatistics.values()" :key="entry.key"
                       :name="$vuetify.lang.t(`$vuetify.map.criticalPopup.title.${entry.key}`) "
                       layer-type="overlay"
                       v-if="isSpecial && !routeStatistics.isEmpty() && entry.value.geometry.coordinates.length > 0">
          <l-geo-json :geojson="entry.value" :options="geoJsonOptions" @click="addPureEventMarker"></l-geo-json>
        </l-layer-group>

        <!-- start and finish icons: travelSelection.travel.route.pathStart -->
        <template v-if="travel.route.pathStart && travel.route.pathFinish">
          <l-marker :lat-lng="travel.route.pathStart" :icon="startIcon">
          </l-marker>
          <l-marker :lat-lng="travel.route.pathFinish" :icon="finishIcon">
          </l-marker>
        </template>

        <template v-if="!clusterEvents">
          <!-- events without clusters -->
          <template v-for="event in events">
            <l-marker :lat-lng="event.point" :key="event.idCriticalEvent" :ref="`marker-${event.idCriticalEvent}`"
                      @click="markerClicked(event.idCriticalEvent)">
              <l-icon :icon-anchor="getMarkerAnchor(event.idCriticalEvent)"
                      :options="{state: event.dangerousStateNormalized}">
                <v-icon :color="event.getColorState()" :size="getMarkerSize(event.idCriticalEvent)">
                  {{
                    icons.hasOwnProperty(event.dangerousStateNormalized) ? icons[event.dangerousStateNormalized] :
                      mdiMapMarker
                  }}
                </v-icon>
              </l-icon>
            </l-marker>
          </template>
        </template>
        <template v-else>
          <!-- critical events markers -->
          <v-marker-cluster v-for="(group, groupIndex) in groupedEvents.groups" :key="`clusterRef-${groupIndex}`"
                            :ref="`clusterRef-${groupIndex}`"
                            :options="{iconCreateFunction: clusterIcon, spiderfyOnMaxZoom: false, disableClusteringAtZoom: 18}">
            <template v-for="event in group">
              <l-marker :lat-lng="event.point" :key="event.idCriticalEvent"
                        @click="markerClicked(event.idCriticalEvent)"
                        :options="{state: event.dangerousStateNormalized}"
                        :ref="`marker-${event.idCriticalEvent}`">
                <l-icon :icon-anchor="getMarkerAnchor(event.idCriticalEvent)">
                  <v-icon :color="event.getColorState()" :size="getMarkerSize(event.idCriticalEvent)">
                    {{
                      icons.hasOwnProperty(event.dangerousStateNormalized) ? icons[event.dangerousStateNormalized] :
                        mdiMapMarker
                    }}
                  </v-icon>
                </l-icon>
              </l-marker>
            </template>
          </v-marker-cluster>

          <template v-for="event in groupedEvents.single">
            <l-marker :lat-lng="event.point" :key="event.idCriticalEvent" :ref="`marker-${event.idCriticalEvent}`"
                      @click="markerClicked(event.idCriticalEvent)" :options="{state: event.dangerousStateNormalized}">
              <l-icon :icon-anchor="getMarkerAnchor(event.idCriticalEvent)">
                <v-icon :color="event.getColorState()" :size="getMarkerSize(event.idCriticalEvent)">
                  {{
                    icons.hasOwnProperty(event.dangerousStateNormalized) ? icons[event.dangerousStateNormalized] :
                      mdiMapMarker
                  }}
                </v-icon>
              </l-icon>
            </l-marker>
          </template>
        </template>

      </l-map>
    </v-card-text>
  </v-card>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import { LMap, LTileLayer, LMarker, LControlLayers, LIcon, LGeoJson, LLayerGroup } from 'vue2-leaflet'
import { divIcon, icon, point } from 'leaflet'
import haversine from 'haversine-distance'

import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'

import { UPDATE_CRITICAL_EVENT_STATE } from '@/store/actions'
import MapHelp from './mapHelp'
import {
  mdiDownload,
  mdiMap,
  mdiMapMarker
} from '@mdi/js'

import { AUTH_MODULE, MAP_PAGE, SETTINGS_MODULE } from '@/store/modules'
import { createNamespacedHelpers } from 'vuex'
import { Travel } from '@/models/travel/Travel'
import { createHelpers } from 'vuex-map-fields'
import { dangerousStateIcons } from '@/utils/icons'
import { RouteAdditionalStatistics } from '@/models/travel/RouteAdditionalStatistics'
import PurePointCard from '@/components/map/purePointCard'
import TooltipBasic from '@/components/utils/tooltipBasic'
import CheckboxIconedBasic from '@/components/map/criticalEventsLocalFilters/checkboxIconedBasic'
import { localizeState } from '@/models/map/CriticalEvent'
import CriticalEventsLocalFiltersMenu from '@/components/map/criticalEventsLocalFilters/criticalEventsLocalFiltersMenu'

const { mapActions } = createNamespacedHelpers(MAP_PAGE)

const { mapFields: authFields } = createHelpers({
  getterType: `${AUTH_MODULE}/getField`
})

const { mapFields: settingsFields } = createHelpers({
  getterType: `${SETTINGS_MODULE}/getField`
})

const markerSize = 36

const getParentAtCurrentZoom = (currentZoom, marker) => {
  while (marker.__parent && marker.__parent._zoom >= currentZoom) {
    marker = marker.__parent
  }
  return marker
}

const getClosetPurePoint = (purePoints, marker) => {
  let result = purePoints.length > 0 ? Object.assign({}, purePoints[0]) : null

  if (purePoints.length > 1) {
    let distance = haversine(marker, purePoints[0].point)

    for (const point of purePoints) {
      let newDistance = haversine(marker, point.point)

      if (newDistance < distance) {
        distance = newDistance
        result = Object.assign({}, point)
      }
    }
  }

  return result
}

export default {
  name: 'travelMap',
  components: {
    CriticalEventsLocalFiltersMenu,
    CheckboxIconedBasic,
    TooltipBasic,
    PurePointCard,
    MapHelp,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LGeoJson,
    LControlLayers,
    LLayerGroup,
    'v-marker-cluster': Vue2LeafletMarkerCluster
  },
  data () {
    return {
      url: 'http://{s}.tile.osm.org/{z}/{x}/{y}.png', // 'https://api.maptiler.com/maps/basic/{z}/{x}/{y}.png?key=yuE4oUfE0YGGqWIvya17', // 'http://{s}.tile.osm.org/{z}/{x}/{y}.png', // 'https://api.maptiler.com/tiles/v3-4326/{z}/{x}/{y}.pbf?key=yuE4oUfE0YGGqWIvya17', //'http://{s}.tile.osm.org/{z}/{x}/{y}.png',
      zoom: 10,
      color: 'blue',
      attribution:
      // '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://www.maptiler.com/cloud">MapTiler</a>',
        '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      startIcon: icon({
        iconUrl: require('@/assets/marker_start.png'),
        iconSize: [42, 62],
        iconAnchor: [21, 62]
      }),
      finishIcon: icon({
        iconUrl: require('@/assets/marker_stop.png'),
        iconSize: [42, 62],
        iconAnchor: [21, 62]
      }),

      geoJsonOptions: {
        onEachFeature (feature, layer) {
          layer.setStyle(feature.properties.style)
        }
      },

      mdiMap: mdiMap,
      mdiMapMarker: mdiMapMarker,
      downloadIcon: mdiDownload,
      icons: dangerousStateIcons(),
      rawValues: false,
      selectedEvent: null,
      tripPointsMarkers: [],
      selectedTripPoints: []
    }
  },
  props: {
    travel: {
      type: Travel,
      default: () => new Travel()
    },
    events: Array,
    groupedEvents: Object,
    routeStatistics: RouteAdditionalStatistics,
    tripPoints: Array,
    tripPointsLoading: Boolean,
    eventsLocalFilter: Object
  },
  computed: {
    ...authFields(['isAdmin', 'isSpecial', 'isDebugView']),
    ...settingsFields(['mapSettings.hideReviewedEvents', 'mapSettings.clusterEvents', 'mapSettings.clusterEventTimePeriod']),
    path () {
      return this.travel.route.path
    },
    mapRowHeight () {
      return this.$refs.mapRow.clientHeight
    },
    isDebugAvailable () {
      return this.isAdmin || this.isDebugView
    }
  },
  methods: {
    ...mapActions([UPDATE_CRITICAL_EVENT_STATE]),
    markerClicked (criticalEventId, cluster = null) {
      this.clearEvent()

      this.selectedEvent = criticalEventId
      this.$emit('critical-event-request', criticalEventId)
    },
    clearEvent () {
      if (this.selectedEvent) {
        this.selectedEvent = null
        this.$emit('critical-event-closing')
      }
    },
    getMarkerSize (criticalEventId) {
      return criticalEventId === this.selectedEvent ? 2 * markerSize : markerSize
    },
    getMarkerAnchor (criticalEventId) {
      return criticalEventId === this.selectedEvent ? [markerSize, 2 * markerSize] : [markerSize / 2, markerSize]
    },
    openCriticalEventManually (idCriticalEvent) {
      const markerRef = `marker-${idCriticalEvent}`
      const markerElement = this.$refs[markerRef][0].mapObject

      // this.clearEvent()

      // open popup
      if (this.clusterEvents) {
        // 1. find element in cluster, and call zoomToShowLayer
        // 2. if element is standalone -> just popup it with openPopup
        const eventClusters = this.groupedEvents.eventClusters
        if (idCriticalEvent in eventClusters) {
          // const cluster = getParentAtCurrentZoom(this.$refs['map'].mapObject.getZoom(), markerElement)
          // cluster.unspiderfy()
          // cluster.spiderfy()

          const ref = `clusterRef-${eventClusters[idCriticalEvent]}`
          this.$refs[ref][0].mapObject.zoomToShowLayer(markerElement, () => {
            this.markerClicked(idCriticalEvent, getParentAtCurrentZoom(this.$refs['map'].mapObject.getZoom(), markerElement))
          })
        } else {
          this.$refs['map'].mapObject.panTo(markerElement.getLatLng())
          this.markerClicked(idCriticalEvent)
        }
      } else {
        this.$refs['map'].mapObject.panTo(markerElement.getLatLng())
        this.markerClicked(idCriticalEvent)
      }
    },
    clusterIcon (cluster) {
      const state = cluster.getAllChildMarkers()[0].options.state

      return divIcon({
        html: `<div class="d-inline-flex">
                <span class="title black--text clusterCircle" style="height: 36px; width: 36px; line-height: 36px;">${cluster.getChildCount()}</span>
                <span aria-hidden="true" class="v-icon v-icon--svg black--text" style="font-size: 36px; height: 36px; width: 36px;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36px" height="36px" viewBox="0 0 24 24" role="img" aria-hidden="true">
                        <path d="${this.icons[state]}"></path>
                    </svg>
               </span></div>`,
        className: 'marker-cluster',
        iconSize: point(40, 40)
      })
    },
    addPureEventMarker (event) {
      if (this.tripPoints.length > 0 && this.isDebugAvailable) {
        if (this.tripPointsMarkers.length > 1) {
          this.removeMarker(1)
        }

        this.tripPointsMarkers.push(event.latlng)
        this.selectedTripPoints.push(
          getClosetPurePoint(this.tripPoints, event.latlng)
        )
      }

    },
    removeMarker (index) {
      if (this.tripPoints.length > 0 && this.isDebugAvailable) {
        this.selectedTripPoints.splice(index, 1)
        this.tripPointsMarkers.splice(index, 1)
      }
    },
    clearAllPurePoints () {
      this.selectedTripPoints = []
      this.tripPointsMarkers = []
    }
  },
  created () {
    this['localizeState'] = localizeState

    this.$nextTick(() => {
      this.$refs.map.mapObject.fitBounds(this.$refs.map_geojson.getBounds())

      if (!this.travel.activeCriticalEvent && this.events.length > 0) {
        this.travel.activeCriticalEvent = this.events[0].idCriticalEvent
      }

      const markerRef = `marker-${this.travel.activeCriticalEvent}`

      if (this.travel.activeCriticalEvent && this.$refs.hasOwnProperty(markerRef)) {
        this.openCriticalEventManually(this.travel.activeCriticalEvent)
      }

    })
  },
  watch: {
    path (value) {
      this.$nextTick(() => {
        this.$refs.map.mapObject.fitBounds(this.$refs.map_geojson.getBounds())
      })
    },
    rawValues: {
      immediate: true,
      handler (value) {
        this.$emit('raw-values-changed', value)
      }
    }
  }
}
</script>

<style>
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";

/* redefine 301 px barrier in leaflet on global level */
.leaflet-popup-content {
  width: auto !important;
  margin: 5px;
}
</style>

<style scoped>
.mapSize {
  height: 80vh;
  /*width: 100%;*/
}

.clusterCircle {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  text-align: center;
  background: linear-gradient(orange, red)
}
</style>
